import { writable } from 'svelte/store'
import { getLocation, location, locationToUrl, navigate } from './location'
import { type PanelEntry, makeEntry, readEntry, restoreScroll, trackedScroll, trackScroll } from './panelscroll'
import { type AnyComponent, type Location, type PopupAlignment } from './types'

export interface PanelProps {
  component: AnyComponent
  _id: string
  _class: string
  element?: PopupAlignment
  rightSection?: AnyComponent
  refit?: () => void
}

export const panelstore = writable<{
  panel?: PanelProps | undefined
}>({ panel: undefined })
let currentLocation: string | undefined

// AlexStaff (B-693 п.4): крестик панели возвращает туда, откуда в неё пришли.
// Стек хранит ПОЛНЫЙ предыдущий адрес (путь + фрагмент), чтобы вернуться и на другую страницу (документ), и к другой панели.
const MAX_PANEL_HISTORY = 3
let panelHistory: PanelEntry[] = []
// адрес, на который мы сами только что вернулись крестиком; живёт до первого же события и сам не залипает
let expectedReturn: string | undefined
let curLoc: Location | undefined
let prevLoc: Location | undefined

// Стек переживает обновление страницы: sessionStorage свой у каждой вкладки, ключ — по workspace.
// Вместе со стеком хранится адрес (`at`), на котором он собирался; не совпал при подъёме — стек устарел.
const storageKey = (loc: Location): string => `alexstaff-panel-history:${loc.path[1] ?? ''}`

function persist (): void {
  if (curLoc === undefined) return
  try {
    if (panelHistory.length === 0) sessionStorage.removeItem(storageKey(curLoc))
    else sessionStorage.setItem(storageKey(curLoc), JSON.stringify({ at: locationToUrl(curLoc), stack: panelHistory }))
  } catch {}
}

function restore (loc: Location): void {
  try {
    const raw = sessionStorage.getItem(storageKey(loc))
    if (raw === null) return
    const data = JSON.parse(raw)
    if (data?.at === locationToUrl(loc) && Array.isArray(data.stack)) {
      const entries: Array<PanelEntry | undefined> = data.stack.map(readEntry)
      if (entries.every((e: PanelEntry | undefined) => e !== undefined)) panelHistory = entries.slice(-MAX_PANEL_HISTORY)
      else sessionStorage.removeItem(storageKey(loc))
    } else {
      sessionStorage.removeItem(storageKey(loc))
    }
  } catch {}
}

trackScroll(() => (curLoc !== undefined ? locationToUrl(curLoc) : undefined))

location.subscribe((loc) => {
  if (expectedReturn !== undefined && locationToUrl(loc) !== expectedReturn) expectedReturn = undefined
  if (curLoc === undefined) {
    curLoc = loc
    restore(loc)
  } else if (locationToUrl(loc) !== locationToUrl(curLoc)) {
    prevLoc = curLoc
    curLoc = loc
    persist()
  }
})

// true, если текущий адрес — тот, куда нас только что вернул крестик; флаг одноразовый
function isReturn (): boolean {
  const res = expectedReturn !== undefined && curLoc !== undefined && locationToUrl(curLoc) === expectedReturn
  expectedReturn = undefined
  return res
}

function rememberFrom (from: Location | undefined): void {
  if (from === undefined || curLoc === undefined) return
  if (from.path[1] !== curLoc.path[1]) return
  if (locationToUrl(from) === locationToUrl(curLoc)) return
  panelHistory.push(makeEntry(from, trackedScroll(locationToUrl(from))))
  if (panelHistory.length > MAX_PANEL_HISTORY) panelHistory.shift()
  persist()
}

export function getPanelURI (component: AnyComponent, _id: string, _class: string, element?: PopupAlignment): string {
  const panelProps = [component, _id, _class]
  if (typeof element === 'string') {
    panelProps.push(element)
  }
  return encodeURIComponent(panelProps.join('|'))
}

export function showPanel (
  component: AnyComponent,
  _id: string,
  _class: string,
  element?: PopupAlignment,
  rightSection?: AnyComponent,
  doNavigate: boolean = true
): void {
  openPanel(component, _id, _class, element, rightSection, doNavigate ? curLoc : prevLoc)
  const loc = getLocation()
  if (doNavigate && loc.fragment !== currentLocation) {
    loc.fragment = currentLocation
    navigate(loc)
  }
}

function openPanel (
  component: AnyComponent,
  _id: string,
  _class: string,
  element?: PopupAlignment,
  rightSection?: AnyComponent,
  from?: Location
): void {
  const newLoc = getPanelURI(component, _id, _class, element)
  if (currentLocation === newLoc) {
    return
  }
  const wasReturn = isReturn()
  const top = panelHistory[panelHistory.length - 1]
  if (wasReturn) {
    // возврат по крестику — стек не пополняем (иначе зацикливание)
  } else if (top !== undefined && curLoc !== undefined && locationToUrl(top.loc) === locationToUrl(curLoc)) {
    // «Назад» браузера на адрес, лежащий в стеке: снимаем его, а не кладём адрес, с которого ушли
    panelHistory.pop()
    persist()
  } else {
    rememberFrom(from)
  }
  currentLocation = newLoc
  panelstore.update(() => {
    return { panel: { component, _id, _class, element, rightSection } }
  })
}

export function closePanel (shouldRedirect: boolean = true): void {
  if (!isReturn()) {
    panelHistory = []
    persist()
  }
  currentLocation = undefined
  panelstore.update(() => {
    return { panel: undefined }
  })
  if (shouldRedirect) {
    const loc = getLocation()
    loc.fragment = undefined
    navigate(loc)
  }
}

/**
 * Закрыть панель, вернувшись на адрес, с которого её открыли. Стек пуст — обычное closePanel().
 */
export function closePanelToPrevious (): void {
  const target = panelHistory.pop()
  persist()
  if (target === undefined) {
    closePanel()
    return
  }
  expectedReturn = locationToUrl(target.loc)
  if (navigate(target.loc)) {
    restoreScroll(target.scroll)
  } else {
    expectedReturn = undefined
    panelHistory = []
    persist()
    closePanel()
  }
}
